import React, { useState, useEffect, MouseEvent, ReactNode } from 'react';
import sessionRepo from '../../core/repo/sessionRepo';
import validSvc from '../../core/validSvc';
import { ngDelegate, ngContext } from '../../core/services/ng.delegate';
import { NgContext } from '../../../global';
import PageTop from '../../core/components/PageTop';

interface WinCrypto {
    randomUUID?: () => string; // typescrypt is missing this method
}
enum Render {
    Init,
    Prompt,
    Err,
}

// context
let _ngCtx = {} as LoginContext;
type LoginContext = {
    isDev: boolean;
    isDebug: boolean;
    hasToken: boolean;
};
function contextVM(ctx: NgContext): LoginContext {
    return {
        isDev: ctx.isDev,
        isDebug: ctx.isDebug,
        hasToken: validSvc.isText(ctx.sessionToken),
    };
}

const VerityLoginCtl = (): JSX.Element => {
    const [render, setRender] = useState<Render>(Render.Init);
    const [errMsg, setErrMsg] = useState<string>('');

    // init
    useEffect(() => {
        console.log('VLOG: init');

        _ngCtx = contextVM(ngContext());

        // reset verity login session
        sessionRepo.setLocalStoreKey('verity_login', { ts: Date.now().toString() });

        // okta return error
        const oktaReturn = sessionRepo.get('okta-return');
        sessionRepo.clearKey('okta-return');
        if (!!oktaReturn && validSvc.isText(oktaReturn.error)) {
            setErrMsg(oktaReturn.error);
            setRender(Render.Err);
            return;
        }

        // already has a session token?
        if (_ngCtx.hasToken) {
            // re-run app start up (this will check token and go to home page or clear session and re-start login)
            console.log('VLOG: Token found');
            ngDelegate.reloadStartUp();
            return;
        }

        // prompt
        setRender(Render.Prompt);

        return destroy;
    }, []);

    function destroy(): void {
        _ngCtx = {} as LoginContext;
    }

    function retryLogin(): void {
        setErrMsg('');
        setRender(Render.Prompt);
    }

    // RENDER MAIN
    const showPrompt = render == Render.Prompt;
    const showErr = render == Render.Err;
    return (
        <div className="login-feature-v2">
            <PageTop
                title="WORKFORCE LOGIN"
                icon="sprite-users"
            />
            <section className="panel">
                <h5>Welcome to Verity Workforce Login</h5>
                <p>Verity employees can now use their Cigna network user name and password.</p>
                <p>&nbsp;</p>
                {showPrompt && <Prompt />}
                {showErr && (
                    <Err
                        errMsg={errMsg}
                        retryLogin={retryLogin}
                    />
                )}
            </section>
        </div>
    );
};

const Prompt = (): JSX.Element => {
    console.log('VLOG: PROMPT');
    return (
        <>
            <Button onClick={redirectToOkta}>LOGIN TO WORKFORCE...</Button>
            {_ngCtx.isDev && (
                <span>
                    <Button
                        cssName="btn-cancel"
                        onClick={resetToMainLogin}
                    >
                        RESET TO MAIN LOGIN
                    </Button>
                </span>
            )}
        </>
    );
};

type ErrProps = {
    errMsg: string;
    retryLogin: () => void;
};

const Err = ({ errMsg, retryLogin }: ErrProps): JSX.Element => {
    console.log('VLOG: ERR');
    return (
        <div className="okta-login-err">
            <h5>
                <span className="icon-glyph icon-err-warn"></span>Login Error
            </h5>
            <p>
                <span>{errMsg}</span>
            </p>
            <p>&nbsp;</p>
            <span>
                <Button onClick={retryLogin}>RETRY</Button>
            </span>
            {_ngCtx.isDev && (
                <span>
                    <Button
                        cssName="btn-cancel"
                        onClick={resetToMainLogin}
                    >
                        RESET TO MAIN LOGIN
                    </Button>
                </span>
            )}
        </div>
    );
};

// HELPERS: plain JS
// note: plain JS does NOT need to be in a component to be called

function verityLoginApi(): string {
    let port = window.location.port;
    if (validSvc.isInteger(port)) {
        port = ':' + port;
    } else {
        port = '';
    }
    return window.location.protocol + '//' + window.location.hostname + port + '/api/okta/login';
}

function resetToMainLogin(): void {
    sessionRepo.clear();
    sessionRepo.clearLocalStore();
    window.location.href = '/';
}

function redirectToOkta(): void {
    // check for webcrypto support
    const winCrypt = window.crypto as WinCrypto; // cast to our Crypto interface
    if (!!!winCrypt || typeof winCrypt.randomUUID == 'undefined') {
        // get user agent for support help
        const ugent = !!window.navigator ? window.navigator.userAgent : '';
        ngDelegate.warn(
            'Cannot Login. Your browser is not supported, please upgrade to a modern browser.',
            'verity.login',
            '',
            'User Agent: ' + ugent,
        );
        return;
    }

    // unique request cookie
    // note: https://www.npmjs.com/package/react-cookie is a commonly used cookie pkg but provides nothing more than below
    const id1 = winCrypt.randomUUID(),
        id2 = winCrypt.randomUUID();
    let cook = 'okta340b=STAFF|' + id1.toString() + '|' + id2.toString();
    cook += ';max-age=' + (60 * 5).toString(); // 5 mins
    cook += ';samesite=none';
    cook += ';secure';
    cook += ';path=/api/okta';
    document.cookie = cook;

    // delay to set cookie and send to api
    setTimeout(() => {
        window.location.replace(verityLoginApi());
    }, 1000);
}

// HELPERS: COMPONENTS (this will be moved later)
type ClickableParams = {
    cssName?: string;
    onClick: () => void;
    children: ReactNode | string | undefined;
};
const Button = ({ cssName, onClick, children }: ClickableParams): JSX.Element => {
    function handleClick(e: MouseEvent): void {
        e.preventDefault();
        onClick();
    }
    return (
        <button
            type="button"
            className={cssName}
            onClick={handleClick}
        >
            {children}
        </button>
    );
};

// const Link = ({ cssName, onClick, children }: ClickableParams) => {
//     function handleClick(e: MouseEvent) {
//         e.preventDefault();
//         onClick();
//     }
//     return (
//         <a className={cssName} onClick={handleClick}>
//             {children}
//         </a>
//     );
// };

export default VerityLoginCtl;
